import dayjs from 'dayjs';
import {useTranslation} from 'react-i18next';
import {DefaultTheme, useTheme} from 'styled-components';
import {ACCESS_TIME_STATUS, BookingSummary, ShortReservation} from '@guestapp/sdk';
import {device} from '@guestapp/ui/src/utils/device';
import {useReservation} from 'context/reservation';
import {useSummary} from 'context/summary';
import {useCustomTheme} from 'context/customTheme';
import {useReservationSettings, useScreenResize} from 'hooks';
import {CustomIcon} from '@guestapp/ui';
import {ReservationNumberBox} from '../CheckInInfoBox/ReservationNumberBox';
import {
  Content,
  DateInfo as StyledDateInfo,
  DatesArrow,
  FullDate,
  Header,
  MainDate,
  Title,
  Wrapper,
} from './styled';

function getShortTime(time?: string) {
  return time?.substring(0, 5);
}

function getFullDate(date: string) {
  return dayjs(date).utc().format('DD MMM YYYY').toUpperCase();
}

const getIconColor = (hasTemplate: boolean, isMobile: boolean, theme: DefaultTheme) => {
  if (!hasTemplate && !isMobile) {
    return '#152946';
  }
  if (isMobile) {
    return theme.colors.branding.boxesModals;
  }
  return theme.colors.branding.fontPrimary;
};

const isNoHours = (summary?: BookingSummary) => {
  return Boolean(
    !summary?.is_remote_access_enabled ||
      summary?.access_time_status === ACCESS_TIME_STATUS.disabled,
  );
};

type TimeSelector = (
  reservation: ShortReservation,
  summary: BookingSummary,
) => {customTime?: string; defaultTime?: string};

const getTime =
  (reservation: ShortReservation, summary: BookingSummary) =>
  (selector: TimeSelector) => {
    if (isNoHours(summary)) {
      return undefined;
    }
    const {customTime, defaultTime} = selector(reservation, summary);

    if (summary.access_time_status === ACCESS_TIME_STATUS.custom) {
      return getShortTime(customTime);
    }
    return getShortTime(defaultTime);
  };

const getDatesInfo = (reservation?: ShortReservation, summary?: BookingSummary) => {
  if (!reservation || !summary) return {};

  return {
    checkIn: {
      mainDate: getTime(
        reservation,
        summary,
      )((reservation, summary) => ({
        customTime: summary?.access_start_time,
        defaultTime: reservation?.access_start_time,
      })),
      fullDate: getFullDate(reservation?.check_in_date),
    },
    checkOut: {
      mainDate: getTime(
        reservation,
        summary,
      )((reservation, summary) => ({
        customTime: summary?.access_end_time,
        defaultTime: reservation?.access_end_time,
      })),
      fullDate: getFullDate(reservation?.check_out_date),
    },
  };
};

function BookingDatesBox() {
  const {t} = useTranslation();
  const {data: reservation} = useReservation();
  const {showReservationNumberBox} = useReservationSettings();
  const {data: summary} = useSummary();
  const {hasTemplate} = useCustomTheme();
  const theme = useTheme();
  const {isMatch: isMobile} = useScreenResize(device.laptopM);
  const datesInfo = getDatesInfo(reservation, summary);

  const iconColor = getIconColor(hasTemplate, isMobile, theme);

  return (
    <Wrapper
      $hasTemplate={hasTemplate}
      className="chekin-booking-box__container relative"
    >
      <Header $hasTemplate={hasTemplate} className="chekin-booking-box__header">
        {t('booking_dates')}
      </Header>
      <Content className="chekin-booking-box__content">
        <StyledDateInfo className="chekin-booking-box__info-chekin">
          <Title className="chekin-booking-box__info-title">{t('check_in_short')}</Title>
          <MainDate
            className="chekin-booking-box__info-hours"
            $isNoHours={isNoHours(summary)}
          >
            {datesInfo.checkIn?.mainDate}
          </MainDate>
          <FullDate
            className="chekin-booking-box__info-date"
            $isNoHours={isNoHours(summary)}
          >
            {datesInfo.checkIn?.fullDate}
          </FullDate>
        </StyledDateInfo>
        <DatesArrow>
          <CustomIcon
            name="arrowDates"
            custom
            color={iconColor}
            className="chekin-booking-box__content-icon"
          />
        </DatesArrow>
        <StyledDateInfo className="chekin-booking-box__info-chekout">
          <Title className="chekin-booking-box__info-title">{t('check_out_short')}</Title>
          <MainDate
            className="chekin-booking-box__info-hours"
            $isNoHours={isNoHours(summary)}
          >
            {datesInfo.checkOut?.mainDate}
          </MainDate>
          <FullDate
            className="chekin-booking-box__info-date"
            $isNoHours={isNoHours(summary)}
          >
            {datesInfo.checkOut?.fullDate}
          </FullDate>
        </StyledDateInfo>
      </Content>
      {showReservationNumberBox && (
        <div className="hidden laptop-ml:block">
          <ReservationNumberBox />
        </div>
      )}
    </Wrapper>
  );
}

export {BookingDatesBox};
